import { Component, DoCheck, OnDestroy, OnInit, signal } from '@angular/core';
import { ChartService } from '../chart.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ChartModule } from 'primeng/chart';
import { TabsModule } from 'primeng/tabs';
import { MenuItem, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { Chart } from '../chart';
import { ButtonModule } from 'primeng/button';
import { ComboChart } from '../model/combo-chart';
import { DateTime } from 'luxon';
import { MonthsLong } from '@shared/enums/months-long';
import { Observable, single, Subject, takeUntil, tap } from 'rxjs';
import { AppService } from 'app/app.service';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { ActivatedRoute } from '@angular/router';

interface TabItem {
  id: number;
  label: string;
}

@Component({
    selector: 'app-combo-chart',
    imports: [
        CommonModule,
        BreadcrumbComponent,
        ChartModule,
        TabsModule,
        ButtonModule
    ],
    templateUrl: './combo-chart.component.html',
    styleUrl: './combo-chart.component.scss'
})

export class ComboChartComponent implements OnInit, DoCheck, OnDestroy {

  barChartData?: Chart;
  lineChartData?: Chart;

  timeByMonth?: ComboChart[];
  options: any;
  lightModeOptions: any;
  darkModeOptions: any;

  tabItems: TabItem[] = [
    { id: 0, label: 'Bar' },
    { id: 1, label: 'Line' }
  ];
  activeIndex = signal<number>(0);

  previousTheme?: string;

  periodTotal: number = 0;
  highestMonth: number = 0;
  periodAverage: number = 0;
  highestMonthLabel: string = '';
  activeYear: number = 0;
  btnDisabledIncrement: boolean = false;
  btnDisabledDecrement: boolean = false;

  theme$?: string;
  destroy$ = new Subject();

  constructor(
    public appService: AppService,
    private messageSerive: MessageService,
    private chartService: ChartService,
    private errorLogService: ErrorLogService,
    private breadcrumbService: BreadcrumbService,
    private route: ActivatedRoute
  ) { }

  chartResponseData$?: Observable<ComboChart[]>;
  
  ngOnInit(): void {
    //update breadcrumb service
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Time by Month';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);
    
    this.appService.theme$.pipe(
      takeUntil(this.destroy$),
      tap( (theme) =>{
        this.theme$ = theme;
      })
    ).subscribe();

    this.activeYear = DateTime.now().year;
    this.btnDisabledIncrement = this.activeYear === DateTime.now().year;

    this.chartResponseData$ = this.chartService.getByMonthLineChartCombo()
    .pipe(
      tap( (chartResponse) => {
        if (!chartResponse) {
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: No response from chart service`);
          this.messageSerive.add({severity:'error', summary: 'Error', detail: 'No chart data found'});
          //this.loadEmptyChart();
          return;
        }
        this.timeByMonth = chartResponse;
        this.barChart(this.activeYear);
        this.lineChart(this.activeYear);
        this.calculateTotals();
      })
    );

    this.lightModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          labels: {
            color: '#35454c'
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#6b7280'
          },
          grid: {
            color: ''
          }
        },
        y: {
          ticks: {
            color: '#6b7280'
          },
          grid: {
            color: '#eeecec'
          },
          max: 100
        }
      }
    };

    this.darkModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          labels: {
            color: '#e7e7e7'
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: "#e7e7e7"
          },
          grid: {
            color: ''
          }
        },
        y: {
          ticks: {
            color: "#e7e7e7"
          },
          grid: {
            color: '#2b2b2b'
          },
          border: {
            color: '#2b2b2b'
          },
          max: 100
        }
      }
    };

    this.options = this.lightModeOptions;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onIncrementYear() {
    const currentYear = DateTime.now().year;
    this.activeYear += 1;
    this.btnDisabledIncrement = this.activeYear === currentYear ? true : false;
    this.barChart(this.activeYear);
    this.lineChart(this.activeYear);
    this.calculateTotals();
  }

  onDecrementYear() {
    const currentYear = DateTime.now().year;
    this.activeYear -= 1;
    this.btnDisabledIncrement = this.activeYear === currentYear ? true : false;
    
    this.barChart(this.activeYear);
    this.lineChart(this.activeYear);
    this.calculateTotals();
  }

  calculateTotals() {
    const filteredForYear = this.timeByMonth?.filter( (item) => item.year === this.activeYear)[0];
    if (filteredForYear) {
      this.periodTotal = filteredForYear.months.data.reduce((a, b) => a + b, 0);
      this.highestMonth = Math.max(...filteredForYear.months.data);
      this.periodAverage = this.periodTotal / 12;
      const highestMonthIndex = filteredForYear.months.data.indexOf(this.highestMonth);
      const highestMonthLabel = filteredForYear.months.label[highestMonthIndex];
      this.highestMonthLabel = MonthsLong[highestMonthLabel - 1];
    }
    else {
      this.periodTotal = 0;
      this.highestMonth = 0;
      this.periodAverage = 0;
      this.highestMonthLabel = '';
      return;
    }
  }

  barChart(year: number) {
    const filteredForYear = this.timeByMonth?.filter( (item) => item.year === year)[0];
    const data = this.validateMonthData(filteredForYear);
    if (filteredForYear) {
      this.barChartData = {
        labels: this.getMonthNames(data?.months.label || []),
        datasets: [
          {
            type: 'bar',
            label: 'Flight Time',
            data: data?.months.data || [],
            backgroundColor: '#1c80cf50',
            borderColor: '#1c80cf',
            hoverBackgroundColor: '#1c80cf',
            borderWidth: 1,
          }
        ]
      };
    }
    else {
      this.barChartData = {
        labels: Array.from({ length: 12 }, (_, i) => MonthsLong[i]),
        datasets: [
          {
            type: 'bar',
            label: 'Flight Time',
            data: Array(12).fill(0),
            backgroundColor: '#1c80cf50',
            borderColor: '#1c80cf',
            hoverBackgroundColor: '#1c80cf',
            borderWidth: 1,
          }
        ]
      };
    }
    // else {
    //   return;
      // this.loadEmptyChart();
      // this.calculateTotals();
    // }
  }

  lineChart(year: number) {
    const filteredForYear = this.timeByMonth?.filter( (item) => item.year === year)[0];
    const data = this.validateMonthData(filteredForYear);
    if (filteredForYear) {
      this.lineChartData = {
        labels: this.getMonthNames(data?.months.label || []),
        datasets: [
          {
            label: 'Flight Time',
            fill: false,
            borderColor: '#1c80cf',
            yAxisID: 'y',
            tension: 0.4,
            data: data?.months.data || []
          }
        ]
      };
    }
    else {
    this.lineChartData = {
        labels: Array.from({ length: 12 }, (_, i) => MonthsLong[i]),
        datasets: [
          {
            type: 'bar',
            label: 'Flight Time',
            data: Array(12).fill(0),
            backgroundColor: '#1c80cf50',
            borderColor: '#1c80cf',
            borderWidth: 1,
          }
        ]
      };
    }
  }

  /**
   * Ensures that the provided `ComboChart` data contains entries for all 12 months of the year.
   * 
   * If the input is `undefined`, returns a default `ComboChart` structure for the current year,
   * with month labels from 1 to 12 and all data values set to 0.
   * 
   * If the input contains data for only some months, fills in missing months with 0 values,
   * returning a new `ComboChart` object with complete month labels and corresponding data.
   * 
   * @param filteredForYear - The `ComboChart` data for a specific year, or `undefined` if no data exists.
   * @returns A `ComboChart` object with labels for all 12 months and data for each month (0 if missing), or `undefined`.
   */
  validateMonthData(filteredForYear: ComboChart | undefined): ComboChart | undefined {
    // If no data for the year, return a default structure with 0s for all months
    if (!filteredForYear) {
      return {
        year: DateTime.now().year,
        months: {
          label: Array.from({ length: 12 }, (_, i) => i + 1),
          data: Array(12).fill(0)
        }
      } as ComboChart;
    }

    const monthsWithData = filteredForYear.months.label; // Get the months present in the data
    const dataForMonths = filteredForYear.months.data; // Get the data corresponding to those months
    const fullLabels = Array.from({ length: 12 }, (_, i) => i + 1); //create array [1, 2, ..., 12]
    // Create a full data array with 0s for missing months
    const fullData = fullLabels.map((month, idx) => {
      const index = monthsWithData.indexOf(month); // Find the index of the month in the present months
      return index !== -1 ? dataForMonths[index] : 0; // If data exists for the month, use it; otherwise, use 0
    });

    return {
      ...filteredForYear,
      months: {
        label: fullLabels,
        data: fullData
      }
    };
  }

  getMonthNames(months: number[]): string[] {
    return months.map( (month) =>  MonthsLong[month - 1] );
  }


  /**
   * Angular lifecycle hook that is called during every change detection run.
   * 
   * This method checks if the application's theme has changed by comparing the current theme (`theme$`)
   * with the previously stored theme (`previousTheme`). If a change is detected, it updates the chart options
   * to match the new theme and refreshes the chart data accordingly.
   * 
   * - If the active chart type is 'Bar', it either formats the bar chart data by month for the active year,
   *   or loads an empty chart and recalculates totals if not grouped by month.
   * - If the active chart type is not 'Bar', it performs similar logic for the line chart.
   * 
   * This ensures that the chart appearance and data remain consistent with the selected theme and chart type.
   */
  ngDoCheck(): void {
    const currentTheme = this.theme$;
    if (this.previousTheme !== currentTheme) {
      this.previousTheme = currentTheme;
      this.options = this.toggleThemeOptions();
      if (this.activeIndex() == 0) {
        if (this.timeByMonth) {
          this.barChart(this.activeYear);
        }
        else {
          this.calculateTotals();
        }
      }
      else {
        if (this.timeByMonth) {
          this.lineChart(this.activeYear);
        }
        else {
          this.calculateTotals();
        }
      }
    }
  }

  toggleThemeOptions(): any {
    if (this.theme$ === 'light') {
      return this.lightModeOptions;
    } else {
      return this.darkModeOptions;
    }
  }

  tabMenu = {
    root: {
      transition: 'background-color 1.0s ease',
    },
    tab: {
      borderWidth: '0 0 1px 0',
      borderColor: '{surface.400}',
      hoverBorderColor: '{surface.400}',
      activeBorderColor: '{surface.400}',
      color: '{surface.500}',
      hoverColor: '{surface.500}',
      activeColor: '{primary.600}',
      padding: '0.75rem',
    },
    activeBar: {
      height: '2px',
      bottom: '0px',
      background: '{primary.600}',
    },
    colorScheme: {
      colorScheme: {
        light: {
          tab: {
            background: '{surface.0}',
            hoverBackground: '{surface.0}',
            activeBackground: '{surface.0}',
          },
        },
        dark: {
          tab: {
            background: '{surface.0}',
            hoverBackground: '{surface.0}',
            activeBackground: '{surface.0}',
          },
        },
      }
    }
  }

}
